import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';

/**
 * 供應商填答排行榜元件
 * 顯示跨專案供應商填答統計，點擊可展開查看專案及問卷詳情
 */
const VendorRankings = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('all');
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'rankings', period],
    queryFn: () => dashboardAPI.getRankings({ period, limit: 100 }),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  const rankings = data?.rankings || [];
  const totalPages = Math.ceil(rankings.length / pageSize);
  const paginatedRankings = rankings.slice((page - 1) * pageSize, page * pageSize);

  const periods = [
    { value: 'all', label: '全部' },
    { value: 'week', label: '本週' },
    { value: 'month', label: '本月' },
    { value: 'quarter', label: '本季' },
    { value: 'year', label: '本年' },
  ];

  // 排名徽章顏色
  const getRankBadge = (rank) => {
    if (rank === 1) return 'bg-yellow-400 text-yellow-900';
    if (rank === 2) return 'bg-gray-300 text-gray-700';
    if (rank === 3) return 'bg-amber-600 text-white';
    return 'bg-gray-100 text-gray-600';
  };

  // 完成度顏色
  const getCompletionColor = (rate) => {
    if (rate === 100) return 'text-green-600';
    if (rate >= 50) return 'text-yellow-600';
    if (rate > 0) return 'text-orange-600';
    return 'text-gray-400';
  };

  const getCompletionBg = (rate) => {
    if (rate === 100) return 'bg-green-100';
    if (rate >= 50) return 'bg-yellow-100';
    if (rate > 0) return 'bg-orange-100';
    return 'bg-gray-100';
  };

  // 問卷狀態顯示
  const getFormStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">已完成</span>;
      case 'pending':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">待填寫</span>;
      case 'sent':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">已發送</span>;
      default:
        return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">未知</span>;
    }
  };

  // 切換展開
  const toggleExpand = (vendorName) => {
    setExpandedVendor(expandedVendor === vendorName ? null : vendorName);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* 標題列 */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <span className="text-xl">🏆</span>
            供應商填答排行
          </h3>
          <div className="flex gap-1">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  period === p.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 內容 */}
      <div className="divide-y divide-gray-100">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            載入失敗：{error.message}
          </div>
        ) : rankings.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-3xl mb-2">📊</div>
            <p className="text-gray-500">尚無填答資料</p>
          </div>
        ) : (
          paginatedRankings.map((vendor, pIdx) => {
            const rankIndex = (page - 1) * pageSize + pIdx;
            return (
            <div key={vendor.name}>
              {/* 供應商行 - 可點擊展開 */}
              <div
                onClick={() => toggleExpand(vendor.name)}
                className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition cursor-pointer ${
                  expandedVendor === vendor.name ? 'bg-purple-50' : ''
                }`}
              >
                {/* 排名 */}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${getRankBadge(
                    rankIndex + 1
                  )}`}
                >
                  {rankIndex + 1}
                </div>

                {/* 供應商資訊 */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {vendor.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {vendor.tax_id || vendor.contact_email || '-'}
                  </div>
                </div>

                {/* 進度條 */}
                <div className="w-24 hidden sm:block">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        vendor.completion_rate === 100 ? 'bg-green-500' :
                        vendor.completion_rate >= 50 ? 'bg-yellow-500' :
                        vendor.completion_rate > 0 ? 'bg-orange-500' : 'bg-gray-300'
                      }`}
                      style={{ width: `${vendor.completion_rate}%` }}
                    />
                  </div>
                </div>

                {/* 統計數字 */}
                <div className="text-right flex-shrink-0">
                  <div className={`font-bold ${getCompletionColor(vendor.completion_rate)}`}>
                    {vendor.completion_rate}%
                  </div>
                  <div className="text-xs text-gray-500">
                    {vendor.total_completed}/{vendor.total_assigned} 份
                  </div>
                </div>

                {/* 狀態細節 */}
                <div className={`px-2 py-1 rounded-lg text-xs font-medium flex-shrink-0 hidden md:block ${getCompletionBg(vendor.completion_rate)}`}>
                  {vendor.completion_rate === 100 ? (
                    <span className="text-green-700">✓ 全部完成</span>
                  ) : (
                    <span className="text-gray-600">{vendor.project_count} 專案</span>
                  )}
                </div>

                {/* 展開箭頭 */}
                <div className="flex-shrink-0">
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedVendor === vendor.name ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* 展開的專案及問卷列表 */}
              {expandedVendor === vendor.name && (
                <div className="bg-gray-50 border-t border-gray-100">
                  {vendor.project_details && vendor.project_details.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {vendor.project_details.map((project) => (
                        <div key={project.project_id} className="px-4 py-3 pl-14">
                          {/* 專案標題 */}
                          <div className="flex items-center justify-between mb-2">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${project.project_id}?tab=forms`);
                              }}
                              className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                            >
                              <span className="text-sm font-medium text-purple-700 hover:underline">
                                {project.project_name}
                              </span>
                              {project.project_code && (
                                <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-600 rounded">
                                  {project.project_code}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {project.completed_count}/{project.total_count} 完成
                            </span>
                          </div>

                          {/* 問卷列表 */}
                          <div className="space-y-1 ml-2">
                            {project.forms.map((form, idx) => (
                              <div
                                key={form.form_id || idx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/projects/${project.project_id}?tab=forms`);
                                }}
                                className="flex items-center justify-between py-1 px-2 rounded hover:bg-purple-100 cursor-pointer transition"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400 text-xs w-4">{idx + 1}.</span>
                                  <span className="text-sm text-gray-700 hover:text-purple-700">{form.form_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {form.submitted_at && (
                                    <span className="text-xs text-gray-400">
                                      {new Date(form.submitted_at).toLocaleDateString('zh-TW')}
                                    </span>
                                  )}
                                  {getFormStatusBadge(form.fill_status)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-4 pl-14 text-gray-400 text-sm">
                      無專案資料
                    </div>
                  )}
                </div>
              )}
            </div>
          );
          })
        )}
      </div>

      {/* 分頁控制 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            顯示 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, rankings.length)} / {rankings.length} 家
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
            >
              上一頁
            </button>
            <span className="px-2 py-1 text-xs text-gray-600">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
            >
              下一頁
            </button>
          </div>
        </div>
      )}

      {/* 底部 */}
      {rankings.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-center">
          <span className="text-xs text-gray-500">
            共 {data?.total || 0} 家供應商（點擊供應商查看專案及問卷明細）
          </span>
        </div>
      )}
    </div>
  );
};

export default VendorRankings;
